import { useState, useEffect } from 'react';
import { FoodfluenceLocation } from '@foodfluence/shared';
import { getLocationsForCampaign } from '../services/api/campaigns';

interface UseCampaignLocationsResult {
  locations: FoodfluenceLocation[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching multiple locations for a campaign
 * @param locationIds - Array of location IDs to fetch
 * @param enabled - Whether to automatically fetch locations (default: true)
 */
export const useCampaignLocations = (
  locationIds: string[] | undefined,
  enabled: boolean = true
): UseCampaignLocationsResult => {
  const [locations, setLocations] = useState<FoodfluenceLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLocations = async () => {
    if (!locationIds || locationIds.length === 0) {
      setLocations([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getLocationsForCampaign(locationIds);

      if (response.success) {
        setLocations(response.data);
      } else {
        setError(response.error || 'Failed to fetch campaign locations');
        setLocations([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      setLocations([]);
      setLoading(false);
      setError(null);
      return;
    }

    fetchLocations();
  }, [JSON.stringify(locationIds), enabled]); // Use JSON.stringify to properly compare arrays

  const refetch = async () => {
    await fetchLocations();
  };

  return { locations, loading, error, refetch };
}; 